<script setup>
import { WdsButtonLink } from '@wds/components';

// ### VARIABLE INITIALIZATION
const props = defineProps({
  propertyTypeObj: {
    type: Object,
    required: true,
  },
  title: {
    type: String,
    required: false,
  },
  popularPlaces: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const { t } = useI18n();

const MAX_NUM_CARDS = 3;

const expanded = ref(false);

// ### COMPUTED
const popularPlacesTitle = computed(() => {
  return props.title || t('t_POPULARPLACESINCITY');
});

const maxNumCards = computed(() => {
  return MAX_NUM_CARDS;
});

// ### METHODS
function toggleExpanded() {
  expanded.value = !expanded.value;
}

function getPropertiesCountText(popularPlace) {
  return t('t_NUMBEROFPROPERTIES', {
    PROPERTYTYPE: t(props.propertyTypeObj[popularPlace.totalPropertiesAvailable === 1 ? 'transcode_s' : 'transcode']),
    NUMPROPERTIES: popularPlace.totalPropertiesAvailable,
  });
}
</script>

<template>
  <section
    v-if="popularPlaces?.length"
    class="popular-places-section"
  >
    <h2
      class="title"
      v-html="popularPlacesTitle"
    />

    <div
      class="popular-places-catalog"
      :class="expanded ? 'expanded' : ''"
    >
      <div
        v-for="(popularPlace, idx) in popularPlaces"
        :key="idx"
        class="popular-places-box"
        :class="{ 'extra-location': idx >= maxNumCards }"
      >
        <CommonHwCardButton
          :full-width="true"
          :href="popularPlace?.url"
          :title="popularPlace.title"
          :aria-label="popularPlace.title"
          rel="noopener"
        >
          <div class="popular-places-container">
            <div class="popular-places-header">
              <div
                class="popular-places-name"
                :title="popularPlace.title"
              >
                {{ popularPlace.name }}
              </div>
              <div
                v-if="popularPlace.pricesFromFormatted"
                class="popular-places-price"
              >
                <span class="price-label">{{ $t('t_PRICESFROM') }}</span>
                <span class="price-value">{{ popularPlace.pricesFromFormatted }}</span>
              </div>
            </div>
            <div
              v-if="popularPlace.totalPropertiesAvailable"
              class="popular-places-properties"
            >
              {{ getPropertiesCountText(popularPlace) }}
            </div>
          </div>
        </CommonHwCardButton>
      </div>
    </div>

    <div
      v-if="popularPlaces.length > maxNumCards"
      class="toggle-container"
    >
      <WdsButtonLink
        rel="noopener"
        class="popular-places-show-more"
        :text="$t(expanded ? 't_SHOWLESS' : 't_SHOWMORE')"
        :icon-end="expanded ? 'chevron-up' : 'chevron-down'"
        @click="toggleExpanded()"
        @keyup.enter="toggleExpanded()"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.popular-places-section {
  padding: 0 $wds-spacing-m;

  h2 {
    @include title-3-bld;

    margin-bottom: $wds-spacing-l;
    color: $wds-color-ink-darker;

    :deep(span) {
      color: $wds-color-purple;
    }
  }

  .popular-places-catalog {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: $wds-spacing-m;
    padding: $wds-spacing-xs 0 $wds-spacing-m 0;

    .popular-places-box {
      &.extra-location {
        display: none;
      }

      &:last-child {
        margin-right: 0;
      }
    }

    &.expanded .popular-places-box.extra-location {
      display: block;
    }
  }

  .popular-places-container {
    display: flex;
    flex-direction: column;
    text-align: left;
    color: $wds-color-ink-darker;

    .popular-places-header {
      display: flex;

      .popular-places-name {
        flex-grow: 1;
        width: 50vw;

        @include title-5-bld;
        @include truncate-text(0, $wds-line-height-title-5);
      }

      .popular-places-price {
        flex-shrink: 0;
        text-align: right;
        margin-left: $wds-spacing-s;

        .price-label {
          color: $wds-color-ink;

          @include body-2-reg;
        }

        .price-value {
          margin-left: $wds-spacing-s;

          @include title-5-bld;
        }
      }
    }

    .popular-places-properties {
      @include body-1-reg;
    }
  }

  .toggle-container {
    display: flex;
    justify-content: right;

    .popular-places-show-more {
      text-transform: capitalize;
    }
  }

  @include tablet {
    .popular-places-container .popular-places-header .popular-places-name {
      width: auto;
    }
  }

  @include desktop {
    padding: 0 wds-rem(40px);

    h2 {
      @include title-1-bld;
    }

    .popular-places-catalog {
      grid-template-columns: 1fr 1fr 1fr;
    }

    .popular-places-container .popular-places-header .popular-places-name {
      width: auto;
    }
  }
}
</style>
